function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useRef, useEffect, useState } from "react";
import classnames from "classnames";
import Carousel from "segments/desktop/core-components/carousel/carousel";
import { generateOnKeyDownHandler } from "client/services/accessibility/accessibility-service";
import { CAROUSEL_ARROW_CLICKED } from "constants/bigbrain-event-types";
import { trackEvent } from "services/bigbrain-service";
import Colors from "styles/colors";
import PrevCarouselArrow from "./prev-carousel-arrow";
import NextCarouselArrow from "./next-carousel-arrow";
import { StyledSlidingCardGridBaseComponent } from "./sliding-card-grid-base-component.scss";
import SlidingCardComponent from "./sliding-card/sliding-card-component";
import CarouselTitleComponent from "./carousel-title/carousel-title-component";
import { ARROW_DESIGN_TYPE } from "segments/desktop/grid/layouts/sliding-card-grid/consts";
import TopRightArrowsGroup from "./TopRightArrowsGroup";
const SlidingCardGridBaseComponent = ({ cards, cardsToShow, cardColor, cardInnerPadding, borderColor, backgroundHoverColor, borderOpacity, arrowsColor, isFullScreen, fullParagraphAdvancedConfig, desktopHorizontalPadding, desktopHorizontalPaddingSmallScreens, disableCardsHoverStyle, slideBreakingPoints, focusOnSelect, sideBySideImage, carouselTitle, removeArrows, carouselTitleAdvancedConfig, desktopVerticalPadding, reverseImageAndText, cardBorderRadius, cardImageSize, cardImageRadius, customItemRenderer, autoplay, autoplaySpeed, infinite = false, isInView, imageOnTop, arrowDesignType, arrowsTopOffset, topRightArrowColor })=>{
    const slider = useRef(null);
    const [isFirstTimeVisible, setIsFirstTimeVisible] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const slideResponsive = useMemo(()=>slideBreakingPoints === null || slideBreakingPoints === void 0 ? void 0 : slideBreakingPoints.map(({ breakpoint, slidesToShow })=>({
                breakpoint: parseInt(breakpoint),
                settings: {
                    slidesToShow: parseFloat(slidesToShow)
                }
            })), [
        slideBreakingPoints
    ]);
    const itemRenderer = (card)=>/*#__PURE__*/ _jsx(SlidingCardComponent, {
            card: card,
            sideBySideImage: sideBySideImage,
            disableHoverStyle: disableCardsHoverStyle,
            fullParagraphAdvancedConfig: fullParagraphAdvancedConfig,
            borderOpacity: borderOpacity,
            borderColor: borderColor,
            backgroundHoverColor: backgroundHoverColor,
            cardColor: cardColor,
            padding: cardInnerPadding,
            reverseImageAndText: reverseImageAndText,
            imageOnTop: imageOnTop
        });
    useEffect(()=>{
        if (isInView && !isFirstTimeVisible) {
            setIsFirstTimeVisible(true);
        }
    }, [
        isInView,
        isFirstTimeVisible
    ]);
    useEffect(()=>{
        if (autoplay) {
            if (isPaused) {
                var _slider_current;
                (_slider_current = slider.current) === null || _slider_current === void 0 ? void 0 : _slider_current.pause();
            } else {
                var _slider_current1;
                (_slider_current1 = slider.current) === null || _slider_current1 === void 0 ? void 0 : _slider_current1.play();
            }
        }
    }, [
        isPaused
    ]);
    const areCardsToShowUnevenNumber = useMemo(()=>Number(cardsToShow) < cards.length && !Number.isInteger(Number(cardsToShow)), [
        cards.length,
        cardsToShow
    ]);
    const PrevArrow = ({ className, onClick })=>{
        const onPrevArrowClick = ()=>{
            if (onClick) {
                trackEvent(CAROUSEL_ARROW_CLICKED, {
                    info3: "prev"
                });
                onClick();
            }
        };
        return /*#__PURE__*/ _jsx("button", {
            type: "button",
            className: classnames(className, "carousel-arrow-wrapper", "prev"),
            onClick: onPrevArrowClick,
            tabIndex: 0,
            onKeyDown: generateOnKeyDownHandler(onClick),
            children: /*#__PURE__*/ _jsx(PrevCarouselArrow, {
                isDisabled: !onClick,
                color: Colors[arrowsColor]
            })
        });
    };
    const NextArrow = ({ className, onClick })=>{
        const onNextArrowClick = ()=>{
            if (onClick) {
                trackEvent(CAROUSEL_ARROW_CLICKED, {
                    info3: "next"
                });
                onClick();
            }
        };
        return /*#__PURE__*/ _jsx("button", {
            type: "button",
            className: classnames(className, "carousel-arrow-wrapper", "next"),
            onClick: onNextArrowClick,
            tabIndex: 0,
            onKeyDown: generateOnKeyDownHandler(onClick),
            children: /*#__PURE__*/ _jsx(NextCarouselArrow, {
                isDisabled: !onClick,
                color: Colors[arrowsColor]
            })
        });
    };
    const carouselProps = {
        items: cards,
        focusOnSelect,
        itemRenderer: customItemRenderer || itemRenderer,
        defaultIndex: 0,
        infinite: infinite,
        centerMode: false,
        slidesToShow: Number(cardsToShow) || undefined,
        centerPadding: "15%",
        arrows: !removeArrows,
        swipeToSlide: true,
        autoplay: isFirstTimeVisible && autoplay,
        autoplaySpeed
    };
    // Don't show arrows in carousel if arrows are on top right
    if (arrowDesignType !== ARROW_DESIGN_TYPE.ARROWS_ON_TOP_RIGHT) {
        carouselProps.prevArrow = /*#__PURE__*/ _jsx(PrevArrow, {});
        carouselProps.nextArrow = /*#__PURE__*/ _jsx(NextArrow, {});
    }
    return /*#__PURE__*/ _jsxs(StyledSlidingCardGridBaseComponent, {
        cardBorderRadius: cardBorderRadius,
        cardImageSize: cardImageSize,
        cardImageRadius: cardImageRadius,
        desktopHorizontalPadding: desktopHorizontalPadding,
        desktopHorizontalPaddingSmallScreens: desktopHorizontalPaddingSmallScreens,
        desktopVerticalPadding: desktopVerticalPadding,
        arrowsTopOffset: arrowsTopOffset,
        className: classnames("sliding-card-grid-component", {
            "full-carousel-width": areCardsToShowUnevenNumber,
            "full-screen": isFullScreen,
            "disable-hover-effect": disableCardsHoverStyle,
            "with-title": !!(carouselTitle === null || carouselTitle === void 0 ? void 0 : carouselTitle.title),
            "infinite-mode": infinite,
            "edge-arrows": arrowDesignType === ARROW_DESIGN_TYPE.ARROWS_ON_TOP_RIGHT
        }),
        children: [
            arrowDesignType === ARROW_DESIGN_TYPE.ARROWS_ON_TOP_RIGHT && /*#__PURE__*/ _jsx(TopRightArrowsGroup, {
                onPrev: ()=>{
                    var _slider_current;
                    return (_slider_current = slider.current) === null || _slider_current === void 0 ? void 0 : _slider_current.prev();
                },
                onNext: ()=>{
                    var _slider_current;
                    return (_slider_current = slider.current) === null || _slider_current === void 0 ? void 0 : _slider_current.next();
                },
                topOffset: arrowsTopOffset,
                arrowColor: Colors[topRightArrowColor]
            }),
            (carouselTitle === null || carouselTitle === void 0 ? void 0 : carouselTitle.title) && /*#__PURE__*/ _jsx(CarouselTitleComponent, {
                visibleCards: cardsToShow,
                title: _object_spread({}, carouselTitle, carouselTitleAdvancedConfig),
                onPrevClick: ()=>{
                    var _slider_current;
                    return slider === null || slider === void 0 ? void 0 : (_slider_current = slider.current) === null || _slider_current === void 0 ? void 0 : _slider_current.prev();
                },
                onNextClick: ()=>{
                    var _slider_current;
                    return slider === null || slider === void 0 ? void 0 : (_slider_current = slider.current) === null || _slider_current === void 0 ? void 0 : _slider_current.next();
                },
                setIsPaused: setIsPaused
            }),
            /*#__PURE__*/ _jsx(Carousel, _object_spread_props(_object_spread({
                ref: slider
            }, carouselProps), {
                responsive: slideResponsive
            }))
        ]
    });
};
export default SlidingCardGridBaseComponent;
